import * as React from "react";

import { Plus, X } from "../icons";
import { Button } from "../button";
import { cn } from "../utils";
import {
  FilterMenu,
  sameValueSet,
  type FilterField,
  type ViewMenuOption,
} from "./view-menu";

/**
 * FilterBar — what's currently applied, stated in the open, above the rows.
 *
 * The funnel menu answers "what CAN I filter by?"; it is the wrong place to read
 * back "what AM I filtered to?" — that answer was hidden behind a click, spread
 * across five submenus, and summarised by a single dot on the trigger. So the
 * applied set gets a surface of its own: one chip per active field, reading
 * `<field> is <value>`, each removable on the spot, with `+` to add another and
 * `Clear` to drop the lot.
 *
 * It is driven by the SAME `FilterField[]` the menu takes — not a parallel list
 * of "active filters" the page assembles by hand. That is the whole point: a
 * field added to the spec shows up in the menu AND in the bar, and the two cannot
 * disagree about what's on.
 *
 * ```tsx
 * <FilterBar fields={filterFields} onReset={clearAll} />
 * ```
 */

export interface FilterBarProps {
  /** The same spec `<FilterMenu>` receives. */
  fields: FilterField[];
  /** Drop every applied filter. The `Clear` button is hidden without it. */
  onReset?: () => void;
  /**
   * Persist the current filter set as a named view. The `Save` button only
   * renders when this is given — a Save that does nothing is worse than none.
   */
  onSave?: () => void;
  saveLabel?: string;
  className?: string;
}

/** The chip's own vocabulary: what's set on a field, in display terms. */
interface AppliedChip {
  id: string;
  label: string;
  icon?: React.ComponentType<{ className?: string }>;
  /** The relation word between field and value ("is", "is any of"). */
  verb: string;
  /** The value side — a labelled option renders its glyph, plain text doesn't. */
  values: ViewMenuOption[];
  /** Fallback when the value isn't an option (a date range, a count). */
  text?: string;
  onRemove?: () => void;
}

function chipFor(field: FilterField): AppliedChip | null {
  switch (field.kind) {
    case "multi": {
      if (field.values.length === 0) return null;
      // At its landing set the field isn't a choice the user made.
      if (field.defaultValues && sameValueSet(field.values, field.defaultValues))
        return null;
      const values = field.values.map(
        (v) =>
          field.options.find((o) => o.value === v) ?? { value: v, label: v },
      );
      return {
        id: field.id,
        label: field.label,
        icon: field.icon,
        // "is" for one, "is any of" for several — the chip should read as the
        // predicate it actually is, not as a comma-joined mystery.
        verb: values.length === 1 ? "is" : "is any of",
        values,
        onRemove: () => field.onChange([]),
      };
    }
    case "single": {
      // A non-clearable single field is a MODE, not a filter — the same reason
      // it doesn't count toward the applied dot. It has no chip.
      if (field.value == null || field.clearable === false) return null;
      const opt = field.options.find((o) => o.value === field.value) ?? {
        value: field.value,
        label: field.value,
      };
      return {
        id: field.id,
        label: field.label,
        icon: field.icon,
        verb: "is",
        values: [opt],
        onRemove: () => field.onChange(null),
      };
    }
    case "toggle": {
      if (!field.checked) return null;
      return {
        id: field.id,
        label: field.label,
        icon: field.icon,
        verb: "is",
        values: [{ value: "on", label: "on" }],
        onRemove: () => field.onChange(false),
      };
    }
    case "custom": {
      if (field.active !== true) return null;
      return {
        id: field.id,
        label: field.label,
        icon: field.icon,
        verb: "is",
        values: [],
        text: field.summary ?? "set",
        onRemove: field.onClear,
      };
    }
  }
}

/** A hairline-divided segment inside a chip. */
function Seg({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <span
      className={cn(
        "flex items-center gap-1.5 px-2 py-1 type-subtext",
        // Dividers between segments, not around them — the chip reads as one
        // object made of parts (field | verb | value), like a formula.
        "border-l border-border/60 first:border-l-0",
        className,
      )}
    >
      {children}
    </span>
  );
}

export function FilterBar({
  fields,
  onReset,
  onSave,
  saveLabel = "Save",
  className,
}: FilterBarProps) {
  const visible = fields.filter((f) => f.visible !== false);
  const chips = visible
    .map(chipFor)
    .filter((c): c is AppliedChip => c !== null);

  // Nothing applied → no bar. It's a read-out of state, so with no state it is
  // just a strip of chrome taking a row of vertical space away from the rows.
  if (chips.length === 0) return null;

  return (
    <div
      data-slot="filter-bar"
      className={cn(
        "flex flex-wrap items-center gap-2 border-b border-border/40 px-4 py-2",
        className,
      )}
    >
      {chips.map((chip) => {
        const Icon = chip.icon;
        return (
          <span
            key={chip.id}
            className="flex items-center overflow-hidden rounded-md border border-border/60 bg-surface-2 text-foreground"
          >
            <Seg>
              {Icon ? (
                <Icon className="size-3.5 shrink-0 text-muted-foreground" />
              ) : null}
              {chip.label}
            </Seg>
            <Seg className="text-muted-foreground">{chip.verb}</Seg>
            <Seg>
              {chip.text ??
                chip.values.map((v) => (
                  <span key={v.value} className="flex items-center gap-1.5">
                    {v.mark ? (
                      <span className="inline-flex size-3.5 shrink-0 items-center justify-center">
                        {v.mark}
                      </span>
                    ) : v.icon ? (
                      <v.icon className="size-3.5 shrink-0 text-muted-foreground" />
                    ) : null}
                    <span className="max-w-[12rem] truncate">{v.label}</span>
                  </span>
                ))}
            </Seg>
            {chip.onRemove ? (
              <button
                type="button"
                aria-label={`Remove ${chip.label} filter`}
                onClick={chip.onRemove}
                className="flex h-full items-center border-l border-border/60 px-1.5 py-1 text-muted-foreground transition-colors hover:bg-foreground/[0.06] hover:text-foreground"
              >
                <X className="size-3" />
              </button>
            ) : null}
          </span>
        );
      })}

      {/* `+` is the SAME menu the funnel opens, wearing a different trigger —
          adding a filter from the bar and from the title row must be one code
          path, or they drift the moment a field is added. */}
      <FilterMenu
        fields={fields}
        align="start"
        aria-label="Add filter"
        trigger={
          <Button
            variant="ghost"
            size="icon-tb"
            aria-label="Add filter"
            className="text-muted-foreground"
          >
            <Plus className="size-3.5" />
          </Button>
        }
      />

      <span className="ml-auto flex items-center gap-1">
        {onReset ? (
          <Button variant="ghost" size="sm" onClick={onReset}>
            Clear
          </Button>
        ) : null}
        {onSave ? (
          <Button variant="outline" size="sm" onClick={onSave}>
            {saveLabel}
          </Button>
        ) : null}
      </span>
    </div>
  );
}
